// src/pages/GeneralChatPage.js
//
// General AI chat — admin-gated. The browser talks to Flask only (POST /api/chat);
// Flask relays to the n8n agent and streams the answer back as SSE.
//
// Left column: collapsible conversation history (pin / rename / delete). Flask
// keeps the display copy (surface="general"); n8n owns the generation memory,
// keyed by conversationId.

import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BsClipboard, BsClipboardCheck, BsPinAngleFill } from "react-icons/bs";
import {
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiPaperclip,
  FiX,
  FiFileText,
  FiGlobe,
  FiCpu,
  FiClock,
} from "react-icons/fi";

import {
  streamGeneralChat,
  listConversations,
  loadConversation,
  renameConversation,
  setConversationPinned,
  deleteConversation,
  uploadAttachments,
  deleteAttachment,
} from "../api/generalChatApi";

// Default surface: Aegis Chat on /api/chat + /api/general-chat/*. Research Chat
// passes its own bundle (see ResearchChatPage) so both surfaces run the exact
// same UI -- any difference a user sees between them comes from the retrieval
// path, not from the page having drifted.
const AEGIS_CHAT_API = {
  streamChat: streamGeneralChat,
  listConversations,
  loadConversation,
  renameConversation,
  setConversationPinned,
  deleteConversation,
  uploadAttachments,
  deleteAttachment,
};

const CONV_KEY = "general_chat_conversation_id";

// Response run time as mm:ss (matches Ask & Answer).
function formatRuntimeMs(ms) {
  if (ms == null || Number.isNaN(ms)) return "";
  const totalSec = Math.max(0, Math.round(Number(ms) / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

// Allowed attachment types (documents only, v1 — mirrors the backend allowlist).
const ATTACH_ACCEPT =
  ".pdf,.docx,.xlsx,.txt,.text,.md,.markdown,.csv,.tsv,.json,.log";
const MAX_ATTACHMENTS = 5; // mirrors the backend per-turn limit

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// A small file chip used both in the composer (removable) and on sent messages.
function AttachmentChip({ a, onRemove }) {
  return (
    <span className={"gc-attach-chip" + (a.error ? " gc-attach-error" : "")}>
      <FiFileText className="gc-attach-ico" />
      <span className="gc-attach-name" title={a.filename}>{a.filename}</span>
      {a.error ? (
        <span className="gc-attach-meta">{a.error}</span>
      ) : (
        <span className="gc-attach-meta">
          {formatBytes(a.sizeBytes)}
          {a.truncated ? " · truncated" : ""}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          className="gc-attach-x"
          title="Remove"
          aria-label={`Remove ${a.filename}`}
          onClick={() => onRemove(a)}
        >
          <FiX />
        </button>
      )}
    </span>
  );
}

// Turn inline [n] markers into markdown links when we have a matching source
// URL, so ReactMarkdown renders them as clickable citations.
function linkifyCitations(text, sources) {
  if (!text || !sources || !sources.length) return text || "";
  const byN = new Map(sources.map((s) => [String(s.n), s.url]));
  return text.replace(/\[(\d+)\]/g, (m, n) => {
    const url = byN.get(n);
    return url ? `[[${n}]](${url})` : m;
  });
}

// Numbered, clickable source list rendered under an answer that used web search.
function SourcesList({ sources }) {
  if (!sources || !sources.length) return null;
  return (
    <div className="gc-sources">
      <div className="gc-sources-head">Sources</div>
      <ol className="gc-sources-list">
        {sources.map((s) => (
          <li key={s.n} value={s.n}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}>
              {s.title || s.url}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}

function newConversationId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Animated "searching . . ." indicator (dots cycle while we await the agent).
function SearchingIndicator() {
  const [dots, setDots] = useState("");
  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 400);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="text-muted fst-italic">
      searching
      <span style={{ display: "inline-block", width: "1.5em", textAlign: "left" }}>
        {dots}
      </span>
    </div>
  );
}

export default function GeneralChatPage({
  api = AEGIS_CHAT_API,
  convKey = CONV_KEY,
  attachmentsEnabled = true,
} = {}) {
  const [messages, setMessages] = useState([]); // [{ role, content }]
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [searching, setSearching] = useState(false);
  const [allowWebSearch, setAllowWebSearch] = useState(true);
  const [deepThinking, setDeepThinking] = useState(false);
  const [blockNotice, setBlockNotice] = useState("");
  const [error, setError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [conversationId, setConversationId] = useState(
    () => sessionStorage.getItem(convKey) || newConversationId()
  );

  // History sidebar state
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [menuId, setMenuId] = useState(null); // open kebab menu
  const [renameId, setRenameId] = useState(null);
  const [renameText, setRenameText] = useState("");

  // Pending file attachments for the next turn (documents only, v1).
  const [attachments, setAttachments] = useState([]); // [{id, filename, kind, sizeBytes, truncated}]
  const [attachError, setAttachError] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // attachmentsEnabled=false surfaces have no attachment endpoint, so both the
  // paperclip and the drop target are suppressed rather than failing on use.

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  async function refreshConversations() {
    try {
      setConversations(await api.listConversations());
    } catch {
      /* leave the list as-is on a transient error */
    }
  }

  // On mount: load the history list and restore the current thread (if any).
  useEffect(() => {
    refreshConversations();
    (async () => {
      try {
        const data = await api.loadConversation(conversationId);
        if (data?.messages?.length) {
          setMessages(
            data.messages.map((m) => ({
              role: m.role,
              content: m.content,
              attachments: m.attachments || [],
              sources: m.sources || [],
              timingMs: m.timingMs,
            }))
          );
        }
      } catch {
        /* new/unknown thread — start empty */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the kebab menu on any outside click.
  useEffect(() => {
    if (!menuId) return;
    const close = () => setMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuId]);

  // Persist the thread id so a page reload continues the same n8n memory thread.
  useEffect(() => {
    sessionStorage.setItem(convKey, conversationId);
  }, [conversationId]);

  // Keep the transcript scrolled to the latest content.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, searching]);

  // Abort any in-flight stream on unmount.
  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  // Copy an assistant answer with formatting preserved (text/html + text/plain).
  async function copyAnswer(idx, plainText) {
    const el = document.getElementById(`gc-msg-${idx}`);
    const flash = () => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1200);
    };
    try {
      if (el && navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": new Blob([String(plainText ?? "")], { type: "text/plain" }),
            "text/html": new Blob([el.innerHTML], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(String(plainText ?? ""));
      }
    } catch {
      await navigator.clipboard.writeText(String(plainText ?? ""));
    }
    flash();
  }

  function startNewChat() {
    if (abortRef.current) abortRef.current.abort();
    setMenuId(null);
    setMessages([]);
    setBlockNotice("");
    setError("");
    setSearching(false);
    setSending(false);
    setAttachments([]);
    setAttachError("");
    setConversationId(newConversationId());
  }

  async function openConversation(id) {
    setMenuId(null);
    if (id === conversationId && messages.length) return;
    if (abortRef.current) abortRef.current.abort();
    setError("");
    setBlockNotice("");
    setSearching(false);
    setSending(false);
    try {
      const data = await api.loadConversation(id);
      setMessages(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          attachments: m.attachments || [],
          sources: m.sources || [],
        }))
      );
      setConversationId(id);
    } catch {
      setError("Could not load that conversation.");
    }
  }

  async function togglePin(c) {
    setMenuId(null);
    try {
      await api.setConversationPinned(c.id, !c.pinned);
      refreshConversations();
    } catch {
      /* ignore */
    }
  }

  function beginRename(c) {
    setMenuId(null);
    setRenameId(c.id);
    setRenameText(c.title);
  }

  async function commitRename(id) {
    const t = renameText.trim();
    setRenameId(null);
    if (!t) return;
    try {
      await api.renameConversation(id, t);
      refreshConversations();
    } catch {
      /* ignore */
    }
  }

  async function removeConversation(c) {
    setMenuId(null);
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await api.deleteConversation(c.id);
      if (c.id === conversationId) startNewChat();
      refreshConversations();
    } catch {
      setError("Could not delete that conversation.");
    }
  }

  // --- Attachments (documents only, v1) -----------------------------------
  async function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAttachError("");

    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      setAttachError(`You can attach up to ${MAX_ATTACHMENTS} files per message.`);
      return;
    }
    const batch = incoming.slice(0, room);

    setAttachBusy(true);
    try {
      const { attachments: results = [] } = await api.uploadAttachments(conversationId, batch);
      const ok = results.filter((r) => r.id);
      const failed = results.filter((r) => r.error);
      if (ok.length) setAttachments((prev) => [...prev, ...ok]);
      if (failed.length) {
        setAttachError(failed.map((f) => `${f.filename}: ${f.error}`).join("; "));
      }
    } catch (err) {
      const failed = (err.attachments || []).filter((f) => f.error);
      setAttachError(
        failed.length
          ? failed.map((f) => `${f.filename}: ${f.error}`).join("; ")
          : err.message || "Upload failed."
      );
    } finally {
      setAttachBusy(false);
    }
  }

  function onPickFiles(e) {
    addFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  }

  async function removeAttachment(a) {
    setAttachments((prev) => prev.filter((x) => x.id !== a.id));
    if (a.id) {
      try {
        await api.deleteAttachment(a.id);
      } catch {
        /* best-effort cleanup */
      }
    }
  }

  function onComposerPaste(e) {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  }

  function onComposerDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (!attachmentsEnabled) return;
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  async function handleSend(e) {
    e && e.preventDefault();
    const text = composer.trim();
    const sent = attachments.slice();
    if ((!text && sent.length === 0) || sending || attachBusy) return;

    setError("");
    setBlockNotice("");
    setComposer("");
    setAttachments([]);
    setAttachError("");
    setSending(true);
    setSearching(true);

    const assistantIndex = messages.length + 1;
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text, attachments: sent },
      { role: "assistant", content: "" },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    let firstToken = true;

    await api.streamChat(
      {
        conversationId,
        message: text,
        allowWebSearch,
        deepThinking,
        attachmentIds: sent.map((a) => a.id),
      },
      {
        onMeta: (audit) => {
          if (audit && audit.searchBlocked) {
            setBlockNotice(audit.blockNotice || "Web search was blocked for this turn.");
          }
        },
        onSources: (srcs) => {
          setMessages((prev) => {
            const next = prev.slice();
            const msg = next[assistantIndex];
            if (msg) next[assistantIndex] = { ...msg, sources: srcs };
            return next;
          });
        },
        onToken: (delta) => {
          if (firstToken) {
            firstToken = false;
            setSearching(false);
          }
          setMessages((prev) => {
            const next = prev.slice();
            const msg = next[assistantIndex];
            if (msg) next[assistantIndex] = { ...msg, content: msg.content + delta };
            return next;
          });
        },
        onDone: (info) => {
          setSearching(false);
          setSending(false);
          if (info && info.timingMs != null) {
            setMessages((prev) => {
              const next = prev.slice();
              const msg = next[assistantIndex];
              if (msg) next[assistantIndex] = { ...msg, timingMs: info.timingMs };
              return next;
            });
          }
          refreshConversations(); // pick up new thread / auto-title / new order
        },
        onError: (msg) => {
          setSearching(false);
          setSending(false);
          setError(msg || "Something went wrong.");
          setMessages((prev) => {
            const next = prev.slice();
            const m = next[assistantIndex];
            if (m && m.role === "assistant" && !m.content) next.splice(assistantIndex, 1);
            return next;
          });
          refreshConversations();
        },
        signal: controller.signal,
      }
    );
  }

  return (
    <div className="gc-layout">
      {sidebarOpen ? (
        <aside className="gc-sidebar">
          <div className="gc-sidebar-head">
            <span className="gc-sidebar-title">History</span>
            <div className="gc-sidebar-actions">
              <button className="gc-newchat-btn" title="New chat" onClick={startNewChat}>
                <FiPlus />
                <span>New Chat</span>
              </button>
              <button
                className="gc-icon-btn"
                title="Collapse"
                onClick={() => setSidebarOpen(false)}
              >
                <FiChevronLeft />
              </button>
            </div>
          </div>

          <div className="gc-conv-list">
            {conversations.length === 0 && (
              <div className="gc-empty">No conversations yet.</div>
            )}
            {conversations.map((c) => (
              <div
                key={c.id}
                className={"gc-conv-item" + (c.id === conversationId ? " active" : "")}
                onClick={() => openConversation(c.id)}
              >
                {renameId === c.id ? (
                  <input
                    className="gc-rename-input"
                    autoFocus
                    value={renameText}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(c.id);
                      if (e.key === "Escape") setRenameId(null);
                    }}
                    onBlur={() => commitRename(c.id)}
                  />
                ) : (
                  <>
                    <span className="gc-conv-title">
                      {c.pinned && <BsPinAngleFill className="gc-pin" />}
                      {c.title}
                    </span>
                    <button
                      className="gc-kebab"
                      title="Options"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuId(menuId === c.id ? null : c.id);
                      }}
                    >
                      <FiMoreVertical />
                    </button>
                  </>
                )}

                {menuId === c.id && (
                  <div className="gc-menu" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => togglePin(c)}>
                      {c.pinned ? "Unpin" : "Pin to top"}
                    </button>
                    <button onClick={() => beginRename(c)}>Rename</button>
                    <button className="gc-menu-danger" onClick={() => removeConversation(c)}>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>
      ) : (
        <button
          className="gc-expand"
          title="Show history"
          onClick={() => setSidebarOpen(true)}
        >
          <FiChevronRight />
        </button>
      )}

      <section className="gc-main">
        <div className="gc-main-head">
          <h3 className="mb-0">Aegis Chat</h3>
        </div>

        {blockNotice && (
          <div className="alert alert-warning py-2" role="alert">
            {blockNotice}
          </div>
        )}
        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        <div ref={scrollRef} className="gc-transcript">
          {messages.length === 0 && !searching && (
            <div className="text-muted">Ask anything to start a conversation.</div>
          )}

          {messages.map((m, i) => {
            if (m.role === "assistant" && !m.content) return null;
            return (
              <div key={i} className={`mb-3 ${m.role === "user" ? "text-end" : ""}`}>
                {m.role === "assistant" && m.timingMs != null && (
                  <div className="gc-runtime">
                    <FiClock /> {formatRuntimeMs(m.timingMs)}
                  </div>
                )}
                <div
                  className={
                    "d-inline-block px-3 py-2 aqa-bubble " +
                    (m.role === "user"
                      ? "aqa-bubble-user"
                      : "aqa-bubble-assistant gc-assistant")
                  }
                  style={{ maxWidth: "85%", textAlign: "left" }}
                >
                  {m.role === "assistant" ? (
                    <>
                      <div
                        style={{
                          position: "sticky",
                          top: 6,
                          height: 0,
                          textAlign: "right",
                          zIndex: 2,
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyAnswer(i, m.content);
                          }}
                          title={copiedIdx === i ? "Copied" : "Copy"}
                          aria-label="Copy response"
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 8,
                            border: "1px solid rgba(27,36,48,0.18)",
                            background: "rgba(255,255,255,0.92)",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {copiedIdx === i ? <BsClipboardCheck /> : <BsClipboard />}
                        </button>
                      </div>

                      <div id={`gc-msg-${i}`} style={{ paddingRight: 34 }}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => (
                              // eslint-disable-next-line jsx-a11y/anchor-has-content
                              <a {...props} target="_blank" rel="noopener noreferrer" />
                            ),
                          }}
                        >
                          {linkifyCitations(m.content, m.sources) || "​"}
                        </ReactMarkdown>
                        <SourcesList sources={m.sources} />
                      </div>
                    </>
                  ) : (
                    <>
                      {m.attachments && m.attachments.length > 0 && (
                        <div className="gc-msg-attachments">
                          {m.attachments.map((a, ai) => (
                            <AttachmentChip key={ai} a={a} />
                          ))}
                        </div>
                      )}
                      {m.content && <div>{m.content}</div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {searching && <SearchingIndicator />}
        </div>

        <form
          onSubmit={handleSend}
          className={"gc-composer-wrap" + (dragActive ? " gc-drag" : "")}
          onDragOver={(e) => {
            e.preventDefault();
            if (!dragActive) setDragActive(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={onComposerDrop}
        >
          {attachError && <div className="gc-attach-errline">{attachError}</div>}

          {attachments.length > 0 && (
            <div className="gc-attach-row">
              {attachments.map((a) => (
                <AttachmentChip key={a.id} a={a} onRemove={removeAttachment} />
              ))}
            </div>
          )}

          <div className="gc-composer">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACH_ACCEPT}
              style={{ display: "none" }}
              onChange={onPickFiles}
            />
            {attachmentsEnabled && (
              <button
                type="button"
                className="gc-icon-btn gc-attach-btn"
                title="Attach files (PDF, Word, Excel, text, CSV)"
                aria-label="Attach files"
                disabled={sending || attachBusy}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <FiPaperclip />
              </button>
            )}

            <button
              type="button"
              className={"gc-web-toggle " + (allowWebSearch ? "on" : "off")}
              onClick={() => setAllowWebSearch((v) => !v)}
              aria-pressed={allowWebSearch}
              title={
                allowWebSearch
                  ? "Web search is ON — click to turn off"
                  : "Web search is OFF — click to turn on"
              }
            >
              <FiGlobe />
              <span className="gc-web-label">Web search</span>
              <span className="gc-web-state">{allowWebSearch ? "On" : "Off"}</span>
            </button>

            <button
              type="button"
              className={"gc-web-toggle " + (deepThinking ? "on" : "off")}
              onClick={() => setDeepThinking((v) => !v)}
              aria-pressed={deepThinking}
              title={
                deepThinking
                  ? "Deep think is ON — slower, multi-step research; click to turn off"
                  : "Deep think is OFF — click to turn on multi-step research"
              }
            >
              <FiCpu />
              <span className="gc-web-label">Deep think</span>
              <span className="gc-web-state">{deepThinking ? "On" : "Off"}</span>
            </button>
            <input
              className="form-control"
              placeholder={attachBusy ? "Reading files…" : "Type a message…"}
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onPaste={onComposerPaste}
              disabled={sending}
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={sending || attachBusy || (!composer.trim() && attachments.length === 0)}
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
